import * as THREE from 'three';
import { ARENA_RADIUS, LANE_HALF_WIDTH, LANE_PATHS, NEXUS_Z, SHOP, STRUCTURE_LAYOUT } from '@moba/shared';
import { makeGlowTexture } from './assets.ts';

/**
 * Escenario de la partida. Hay tres entornos originales, con el aire de los
 * escenarios de un brawler de plataformas (colores saturados, formas simples,
 * fondo vivo), uno por modo:
 *
 * - `kingdom` (3v3 · tres líneas): jardines de un castillo de cuento: césped
 *   segado a franjas, caminos de arenisca, muralla almenada con torreones de los
 *   colores de cada equipo, foso de agua, árboles redondos, setos y flores, y
 *   sombras de nubes que pasan.
 * - `sky` (ARAM): templo en ruinas sobre una isla flotante al atardecer: losas de
 *   mármol con juntas doradas, columnas rotas en el borde, acantilado de roca,
 *   mar de nubes debajo y rocas que flotan alrededor.
 * - `training` (entrenamiento): sala holográfica: suelo de rejilla luminosa,
 *   pasarelas de paneles y un muro de luz alrededor.
 *
 * Los entornos se construyen la primera vez que se usan (`setTheme`).
 */
export type ArenaTheme = 'kingdom' | 'sky' | 'training';

export interface Arena {
  group: THREE.Group;
  update(dt: number, focus?: { x: number; z: number }): void;
  /** Muestra sólo el empedrado de esas líneas (ARAM: la central). */
  setActiveLanes(lanes: number[]): void;
  setTheme(theme: ArenaTheme): void;
  /** high: mapa de sombras 2048; low: 1024; off: el sol no proyecta sombras */
  setShadowQuality(quality: 'high' | 'low' | 'off'): void;
  readonly theme: ArenaTheme;
}

/** Libera la memoria de vídeo de un entorno que ya no se usa (las texturas compartidas se quedan). */
function disposeTheme(root: THREE.Object3D): void {
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const materials = mesh.material ? (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) : [];
    for (const material of materials) material.dispose();
    const light = object as THREE.DirectionalLight;
    if (light.isDirectionalLight && light.shadow?.map) {
      light.shadow.map.dispose();
      light.shadow.map = null;
    }
  });
  root.removeFromParent();
}

interface ThemeBuild {
  group: THREE.Group;
  sun: THREE.DirectionalLight;
  background: THREE.Color;
  fog: THREE.Fog | THREE.FogExp2 | null;
  floor: { map: THREE.Texture; color: number; emissiveMap?: THREE.Texture; emissive?: number };
  lane: { map: THREE.Texture; bump?: THREE.Texture; emissiveMap?: THREE.Texture; emissive?: number; roughness: number; metalness: number };
  curb: { color: number; emissive?: number };
  /** suelo exterior (fuera de la arena) visible y su aspecto */
  outer: { map: THREE.Texture; color: number; emissiveMap?: THREE.Texture; emissive?: number } | null;
  ring: number;
  dust: number;
  exposure?: number;
  tick?: (dt: number, time: number) => void;
}

export function createArena(scene: THREE.Scene): Arena {
  const group = new THREE.Group();
  group.name = 'arena';
  scene.add(group);

  const floorMat = new THREE.MeshStandardMaterial({ roughness: 1, metalness: 0 });
  const outerMat = new THREE.MeshStandardMaterial({ roughness: 1, metalness: 0 });
  const laneMat = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0, bumpScale: 1.5 });
  const curbMat = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0 });

  const outer = makeOuterGround(outerMat);
  group.add(outer);
  const rings = makeRings(floorMat, laneMat);
  group.add(rings.group);
  const lanes = makeLanes(laneMat, curbMat);
  group.add(lanes);
  group.add(makeSpawnGlow());
  group.add(makeShops());

  const dust = makeDust();
  group.add(dust.points);

  const themes = new Map<ArenaTheme, ThemeBuild>();
  let current: ArenaTheme | null = null;
  let active: ThemeBuild | null = null;
  let time = 0;
  let shadowQuality: 'high' | 'low' | 'off' = 'high';
  // monedas de las tiendas: se buscan una vez (antes se recorría toda la arena cada frame)
  const coins: THREE.Object3D[] = [];
  group.traverse((o) => {
    if (o.name === 'shop-coin') coins.push(o);
  });

  const applyShadows = (build: ThemeBuild) => {
    const sun = build.sun;
    sun.castShadow = shadowQuality !== 'off';
    const size = shadowQuality === 'high' ? 2048 : 1024;
    if (sun.shadow.mapSize.x !== size) {
      sun.shadow.mapSize.set(size, size);
      sun.shadow.map?.dispose();
      sun.shadow.map = null;
    }
  };

  const arena: Arena = {
    group,
    get theme() {
      return current ?? 'kingdom';
    },
    setActiveLanes(activeLanes: number[]): void {
      for (const child of lanes.children) child.visible = activeLanes.includes(child.userData.lane as number);
    },
    setTheme(theme: ArenaTheme): void {
      if (theme === current) return;
      current = theme;
      // sólo un entorno en memoria: el anterior se libera (se reconstruye si vuelve)
      for (const [name, other] of themes) {
        if (name === theme) continue;
        disposeTheme(other.group);
        themes.delete(name);
      }
      let build = themes.get(theme);
      if (!build) {
        build = theme === 'sky' ? buildSky() : theme === 'training' ? buildTraining() : buildKingdom();
        themes.set(theme, build);
        group.add(build.group);
      }
      applyShadows(build);
      active = build;
      scene.background = build.background;
      scene.fog = build.fog;

      floorMat.map = build.floor.map;
      floorMat.color.setHex(build.floor.color);
      floorMat.emissiveMap = build.floor.emissiveMap ?? null;
      floorMat.emissive.setHex(build.floor.emissive ?? 0);
      floorMat.needsUpdate = true;

      laneMat.map = build.lane.map;
      laneMat.bumpMap = build.lane.bump ?? null;
      laneMat.emissiveMap = build.lane.emissiveMap ?? null;
      laneMat.emissive.setHex(build.lane.emissive ?? 0);
      laneMat.roughness = build.lane.roughness;
      laneMat.metalness = build.lane.metalness;
      laneMat.needsUpdate = true;

      curbMat.color.setHex(build.curb.color);
      curbMat.emissive.setHex(build.curb.emissive ?? 0);

      outer.visible = Boolean(build.outer);
      if (build.outer) {
        outerMat.map = build.outer.map;
        outerMat.color.setHex(build.outer.color);
        outerMat.emissiveMap = build.outer.emissiveMap ?? null;
        outerMat.emissive.setHex(build.outer.emissive ?? 0);
        outerMat.needsUpdate = true;
      }
      for (const material of rings.glowMaterials) material.color.setHex(build.ring);
      (dust.points.material as THREE.PointsMaterial).color.setHex(build.dust);
    },
    setShadowQuality(quality: 'high' | 'low' | 'off'): void {
      shadowQuality = quality;
      if (active) applyShadows(active);
    },
    update(dt: number, focus?: { x: number; z: number }): void {
      time += dt;
      for (const coin of coins) coin.rotation.z += dt * 2.4;
      dust.points.rotation.y += dt * 0.012;
      dust.points.position.y = Math.sin(time * 0.2) * 0.15;
      if (!active) return;
      active.tick?.(dt, time);
      // la luz del sol (y su caja de sombras) sigue a la cámara: sombras en todo el mapa
      if (focus) {
        const sun = active.sun;
        const offset = sun.userData.offset as THREE.Vector3;
        // se mueve a saltos de un téxel del mapa de sombras: sin parpadeo en los bordes
        const texel = (sun.shadow.camera.right - sun.shadow.camera.left) / sun.shadow.mapSize.x;
        const fx = Math.round(focus.x / texel) * texel;
        const fz = Math.round(focus.z / texel) * texel;
        sun.target.position.set(fx, 0, fz);
        sun.position.set(fx + offset.x, offset.y, fz + offset.z);
      }
    },
  };
  arena.setTheme('kingdom');
  return arena;
}

// ============================================================ utilidades

/** Tamaño en unidades del mundo que cubre una repetición de cada textura del suelo. */
const GRASS_TILE = 7;
const STONE_TILE = 3.2;

/** RNG con semilla: la decoración sale igual en todas las partidas. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Dibuja `draw(x, y)` también desplazado ±size para que la textura repita sin costuras. */
function drawWrapped(size: number, x: number, y: number, reach: number, draw: (x: number, y: number) => void): void {
  for (const dx of [-size, 0, size]) {
    for (const dy of [-size, 0, size]) {
      const px = x + dx;
      const py = y + dy;
      if (px < -reach || px > size + reach || py < -reach || py > size + reach) continue;
      draw(px, py);
    }
  }
}

function canvasTexture(canvas: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 8;
  return texture;
}

function makeCanvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D no disponible');
  return [canvas, ctx];
}

function once<T>(make: () => T): () => T {
  let value: T | undefined;
  return () => (value ??= make());
}

/** Distancia de un punto al segmento ab (plano XZ). */
function segmentDistance(p: { x: number; z: number }, a: { x: number; z: number }, b: { x: number; z: number }): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.z - a.z) * abz) / (abx * abx + abz * abz || 1)));
  return Math.hypot(p.x - (a.x + abx * t), p.z - (a.z + abz * t));
}

function laneDistance(p: { x: number; z: number }): number {
  let best = Infinity;
  for (const path of LANE_PATHS) {
    for (let i = 1; i < path.length; i++) best = Math.min(best, segmentDistance(p, path[i - 1], path[i]));
  }
  return best;
}

function structureDistance(p: { x: number; z: number }): number {
  let best = Infinity;
  for (const spec of STRUCTURE_LAYOUT) best = Math.min(best, Math.hypot(p.x - spec.x, p.z - spec.z));
  return best;
}

/** ¿Hueco libre para decorado? (lejos de las líneas, estructuras, tiendas y el centro). */
function freeSpot(p: { x: number; z: number }, laneClear: number, structureClear: number): boolean {
  const r = Math.hypot(p.x, p.z);
  if (r < 7 || r > ARENA_RADIUS - 1.5) return false;
  if (laneDistance(p) < LANE_HALF_WIDTH + laneClear) return false;
  if (structureDistance(p) < structureClear) return false;
  for (const side of [-1, 1]) {
    if (Math.hypot(p.x, p.z - side * NEXUS_Z) < SHOP.radius + 2) return false;
  }
  return true;
}

/**
 * Pone UVs en coordenadas del mundo (x, z) / tile a una geometría ya colocada en el plano XZ:
 * las piezas que se solapan (tramos y codos de los carriles) muestran el mismo dibujo y no parpadean.
 */
function worldUv(geometry: THREE.BufferGeometry, tile: number): THREE.BufferGeometry {
  const pos = geometry.getAttribute('position');
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = pos.getX(i) / tile;
    uv[i * 2 + 1] = pos.getZ(i) / tile;
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geometry;
}

function makeSun(color: number, intensity: number, offset: THREE.Vector3): THREE.DirectionalLight {
  const sun = new THREE.DirectionalLight(color, intensity);
  sun.userData.offset = offset;
  sun.position.copy(offset);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -34;
  sun.shadow.camera.right = 34;
  sun.shadow.camera.top = 34;
  sun.shadow.camera.bottom = -34;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 120;
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.02;
  return sun;
}

/** Lado (unidades) de los sectores en que se parte el decorado instanciado. */
const CHUNK = 24;

/**
 * Decorado instanciado, partido en sectores de CHUNK×CHUNK: cada sector es una
 * InstancedMesh con su esfera envolvente, así Three.js sólo dibuja los sectores
 * que entran en cámara (una sola malla para todo el mapa nunca se recortaba).
 * El decorado no proyecta sombras (`cast`): las sombras dinámicas son para las
 * unidades; los árboles llevan una sombra pintada (`blobShadows`).
 */
function instanced(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  matrices: THREE.Matrix4[],
  colors?: THREE.Color[],
  receive = true,
  cast = false,
): THREE.Group {
  const group = new THREE.Group();
  const cells = new Map<string, number[]>();
  const position = new THREE.Vector3();
  matrices.forEach((matrix, i) => {
    position.setFromMatrixPosition(matrix);
    const key = `${Math.floor(position.x / CHUNK)},${Math.floor(position.z / CHUNK)}`;
    let list = cells.get(key);
    if (!list) cells.set(key, (list = []));
    list.push(i);
  });
  for (const indices of cells.values()) {
    const mesh = new THREE.InstancedMesh(geometry, material, indices.length);
    indices.forEach((index, i) => {
      mesh.setMatrixAt(i, matrices[index]);
      if (colors) mesh.setColorAt(i, colors[index]);
    });
    mesh.castShadow = cast;
    mesh.receiveShadow = receive;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.computeBoundingSphere();
    group.add(mesh);
  }
  return group;
}

/** Textura de sombra pintada: disco oscuro de borde suave. */
const blobTexture = once(() => {
  const size = 64;
  const [canvas, ctx] = makeCanvas(size);
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(0, 0, 0, 0.55)');
  g.addColorStop(0.6, 'rgba(0, 0, 0, 0.3)');
  g.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
});

/** Sombras pintadas bajo el decorado (árboles, columnas): gratis frente al mapa de sombras. */
function blobShadows(spots: Array<{ x: number; z: number; r: number }>): THREE.Group {
  const geometry = new THREE.PlaneGeometry(2, 2);
  geometry.rotateX(-Math.PI / 2);
  const material = new THREE.MeshBasicMaterial({ map: blobTexture(), transparent: true, depthWrite: false });
  // el sol viene del noreste: la sombra cae algo desplazada
  const matrices = spots.map((s) => matrix(s.x - s.r * 0.35, 0.04, s.z + s.r * 0.25, s.r * 1.25, 1, s.r));
  const group = instanced(geometry, material, matrices, undefined, false);
  group.renderOrder = 1;
  return group;
}

const tmpObject = new THREE.Object3D();
function matrix(x: number, y: number, z: number, sx: number, sy = sx, sz = sx, rotY = 0, rotX = 0, rotZ = 0): THREE.Matrix4 {
  tmpObject.position.set(x, y, z);
  tmpObject.rotation.set(rotX, rotY, rotZ);
  tmpObject.scale.set(sx, sy, sz);
  tmpObject.updateMatrix();
  return tmpObject.matrix.clone();
}

// ============================================================ texturas

/** Pasto genérico: base, manchas, briznas y florecillas (con franjas de segado opcionales). */
function grassCanvas(opts: {
  base: string;
  light: string;
  dark: string;
  hue: [number, number];
  sat: [number, number];
  lum: [number, number];
  stripes?: number;
  flowers?: string[];
  flecks?: number;
}): HTMLCanvasElement {
  const size = 512;
  const [canvas, ctx] = makeCanvas(size);
  ctx.fillStyle = opts.base;
  ctx.fillRect(0, 0, size, size);
  if (opts.stripes) {
    // franjas de césped segado: dos tonos alternos a lo ancho de la textura
    ctx.fillStyle = `rgba(255, 255, 210, ${opts.stripes})`;
    ctx.fillRect(0, 0, size / 2, size);
  }
  for (let i = 0; i < 40; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 30 + Math.random() * 70;
    const light = Math.random() < 0.5;
    drawWrapped(size, x, y, r, (px, py) => {
      const g = ctx.createRadialGradient(px, py, 0, px, py, r);
      g.addColorStop(0, light ? opts.light : opts.dark);
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(px - r, py - r, r * 2, r * 2);
    });
  }
  ctx.lineCap = 'round';
  for (let i = 0; i < 9000; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const len = 3 + Math.random() * 7;
    const lean = (Math.random() - 0.5) * 4;
    const h = opts.hue[0] + Math.random() * (opts.hue[1] - opts.hue[0]);
    const s = opts.sat[0] + Math.random() * (opts.sat[1] - opts.sat[0]);
    const l = opts.lum[0] + Math.random() * (opts.lum[1] - opts.lum[0]);
    ctx.strokeStyle = `hsla(${h}, ${s}%, ${l}%, 0.85)`;
    ctx.lineWidth = 1 + Math.random() * 0.8;
    drawWrapped(size, x, y, 12, (px, py) => {
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + lean, py - len);
      ctx.stroke();
    });
  }
  for (let i = 0; i < (opts.flecks ?? 0); i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 2 + Math.random() * 5;
    ctx.fillStyle = `rgba(${200 + Math.random() * 40}, ${195 + Math.random() * 40}, ${185 + Math.random() * 40}, 0.55)`;
    drawWrapped(size, x, y, r, (px, py) => {
      ctx.beginPath();
      ctx.ellipse(px, py, r, r * 0.7, Math.random() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    });
  }
  for (const color of opts.flowers ?? []) {
    for (let i = 0; i < 14; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      ctx.fillStyle = color;
      drawWrapped(size, x, y, 3, (px, py) => {
        ctx.beginPath();
        ctx.arc(px, py, 1.8, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  }
  return canvas;
}

const kingdomGrass = once(() =>
  canvasTexture(
    grassCanvas({
      base: '#4f9e36',
      light: 'rgba(170, 220, 90, 0.28)',
      dark: 'rgba(30, 90, 30, 0.25)',
      hue: [88, 112],
      sat: [50, 72],
      lum: [30, 52],
      stripes: 0.07,
      flowers: ['rgba(255, 250, 225, 0.9)', 'rgba(255, 214, 70, 0.9)', 'rgba(255, 120, 140, 0.8)'],
    }),
    true,
  ),
);

const skyGrass = once(() =>
  canvasTexture(
    grassCanvas({
      base: '#5fa040',
      light: 'rgba(220, 240, 140, 0.3)',
      dark: 'rgba(60, 90, 40, 0.25)',
      hue: [78, 104],
      sat: [45, 66],
      lum: [34, 56],
      flecks: 45,
      flowers: ['rgba(255, 245, 250, 0.9)', 'rgba(255, 190, 220, 0.85)'],
    }),
    true,
  ),
);

/** Losas: hileras trabadas (arenisca) o cuadrícula (mármol). Devuelve color y relieve. */
function pavingTextures(opts: {
  grout: string;
  rows: number;
  cols: number;
  stagger: boolean;
  gap: number;
  radius: number;
  tone: () => string;
  veins?: boolean;
  goldGrout?: boolean;
  moss?: number;
}): { map: THREE.CanvasTexture; bump: THREE.CanvasTexture } {
  const size = 512;
  const [canvas, ctx] = makeCanvas(size);
  const [bumpCanvas, bumpCtx] = makeCanvas(size);
  ctx.fillStyle = opts.grout;
  ctx.fillRect(0, 0, size, size);
  bumpCtx.fillStyle = '#000';
  bumpCtx.fillRect(0, 0, size, size);
  const cellH = size / opts.rows;
  const cellW = size / opts.cols;
  const gap = opts.gap;
  for (let row = 0; row < opts.rows; row++) {
    const offset = opts.stagger && row % 2 === 1 ? cellW / 2 : 0;
    for (let col = 0; col < opts.cols; col++) {
      const x = col * cellW + offset + gap / 2 + (Math.random() - 0.5) * 2;
      const y = row * cellH + gap / 2 + (Math.random() - 0.5) * 2;
      const w = cellW - gap + (Math.random() - 0.5) * 3;
      const hgt = cellH - gap + (Math.random() - 0.5) * 3;
      const fill = opts.tone();
      drawWrapped(size, x, y, cellW, (px, py) => {
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.roundRect(px, py, w, hgt, opts.radius);
        ctx.fill();
        const shade = ctx.createLinearGradient(px, py, px + w * 0.3, py + hgt);
        shade.addColorStop(0, 'rgba(255, 255, 255, 0.14)');
        shade.addColorStop(1, 'rgba(0, 0, 0, 0.16)');
        ctx.fillStyle = shade;
        ctx.fill();
        if (opts.veins) {
          ctx.save();
          ctx.clip();
          for (let v = 0; v < 3; v++) {
            ctx.strokeStyle = `rgba(120, 115, 130, ${0.18 + Math.random() * 0.2})`;
            ctx.lineWidth = 0.6 + Math.random() * 1.2;
            ctx.beginPath();
            let vx = px + Math.random() * w;
            let vy = py;
            ctx.moveTo(vx, vy);
            for (let k = 0; k < 6; k++) {
              vx += (Math.random() - 0.5) * w * 0.35;
              vy += hgt / 6;
              ctx.lineTo(vx, vy);
            }
            ctx.stroke();
          }
          ctx.restore();
        }
        bumpCtx.fillStyle = '#fff';
        bumpCtx.beginPath();
        bumpCtx.roundRect(px, py, w, hgt, opts.radius);
        bumpCtx.fill();
      });
    }
  }
  if (opts.goldGrout) {
    // juntas doradas: líneas finas sobre la junta
    ctx.strokeStyle = 'rgba(232, 190, 90, 0.85)';
    ctx.lineWidth = 2;
    for (let row = 0; row <= opts.rows; row++) {
      ctx.beginPath();
      ctx.moveTo(0, row * cellH);
      ctx.lineTo(size, row * cellH);
      ctx.stroke();
    }
    for (let col = 0; col <= opts.cols; col++) {
      ctx.beginPath();
      ctx.moveTo(col * cellW, 0);
      ctx.lineTo(col * cellW, size);
      ctx.stroke();
    }
  }
  for (let i = 0; i < 4000; i++) {
    const dark = Math.random() < 0.55;
    ctx.fillStyle = dark ? `rgba(40, 32, 24, ${Math.random() * 0.18})` : `rgba(255, 250, 235, ${Math.random() * 0.16})`;
    ctx.fillRect(Math.random() * size, Math.random() * size, 1.5, 1.5);
  }
  for (let i = 0; i < (opts.moss ?? 0); i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    drawWrapped(size, x, y, 8, (px, py) => {
      ctx.fillStyle = 'rgba(80, 130, 50, 0.35)';
      ctx.beginPath();
      ctx.arc(px, py, 2 + Math.random() * 4, 0, Math.PI * 2);
      ctx.fill();
    });
  }
  return { map: canvasTexture(canvas, true), bump: canvasTexture(bumpCanvas, false) };
}

const sandstone = once(() =>
  pavingTextures({
    grout: '#8a7656',
    rows: 6,
    cols: 4,
    stagger: true,
    gap: 6,
    radius: 6,
    moss: 30,
    tone: () => {
      const l = 70 + Math.random() * 12;
      return `hsl(${36 + Math.random() * 8}, ${38 + Math.random() * 14}%, ${l}%)`;
    },
  }),
);

const marble = once(() =>
  pavingTextures({
    grout: '#a08a5a',
    rows: 2,
    cols: 2,
    stagger: false,
    gap: 4,
    radius: 2,
    veins: true,
    goldGrout: true,
    tone: () => `hsl(${30 + Math.random() * 20}, ${10 + Math.random() * 12}%, ${86 + Math.random() * 8}%)`,
  }),
);

/** Rejilla holográfica: color (paneles oscuros) y emisivo (líneas cian). */
function gridTextures(opts: { base: string; panel: string; major: number; minor: number; border?: boolean }): {
  map: THREE.CanvasTexture;
  emissive: THREE.CanvasTexture;
} {
  const size = 512;
  const [canvas, ctx] = makeCanvas(size);
  const [glowCanvas, glow] = makeCanvas(size);
  ctx.fillStyle = opts.base;
  ctx.fillRect(0, 0, size, size);
  glow.fillStyle = '#000';
  glow.fillRect(0, 0, size, size);
  const step = size / opts.minor;
  for (let i = 0; i < opts.minor; i++) {
    for (let j = 0; j < opts.minor; j++) {
      ctx.fillStyle = (i + j) % 2 === 0 ? opts.panel : opts.base;
      ctx.fillRect(i * step + 1, j * step + 1, step - 2, step - 2);
    }
  }
  for (let i = 0; i <= opts.minor; i++) {
    const major = i % (opts.minor / opts.major) === 0;
    glow.strokeStyle = major ? 'rgba(120, 230, 255, 1)' : 'rgba(60, 150, 210, 0.55)';
    glow.lineWidth = major ? 4 : 1.5;
    for (const [x0, y0, x1, y1] of [
      [i * step, 0, i * step, size],
      [0, i * step, size, i * step],
    ]) {
      glow.beginPath();
      glow.moveTo(x0, y0);
      glow.lineTo(x1, y1);
      glow.stroke();
    }
  }
  if (opts.border) {
    glow.strokeStyle = 'rgba(255, 200, 90, 0.7)';
    glow.lineWidth = 3;
    glow.strokeRect(step * 0.5, step * 0.5, size - step, size - step);
  }
  return { map: canvasTexture(canvas, true), emissive: canvasTexture(glowCanvas, true) };
}

const trainingFloor = once(() => gridTextures({ base: '#0b1422', panel: '#0e1a2b', major: 2, minor: 8 }));
const trainingLane = once(() => gridTextures({ base: '#16263c', panel: '#1b2e48', major: 1, minor: 4, border: true }));

/** Roca con estratos para los acantilados de la isla flotante. */
const rockTexture = once(() => {
  const size = 256;
  const [canvas, ctx] = makeCanvas(size);
  ctx.fillStyle = '#6d5a4c';
  ctx.fillRect(0, 0, size, size);
  for (let y = 0; y < size; y += 6 + Math.random() * 14) {
    const l = 30 + Math.random() * 25;
    ctx.fillStyle = `hsl(${20 + Math.random() * 15}, ${18 + Math.random() * 14}%, ${l}%)`;
    ctx.fillRect(0, y, size, 3 + Math.random() * 10);
  }
  for (let i = 0; i < 2500; i++) {
    ctx.fillStyle = `rgba(${Math.random() < 0.5 ? '20, 14, 10' : '230, 210, 190'}, ${Math.random() * 0.2})`;
    ctx.fillRect(Math.random() * size, Math.random() * size, 2, 2);
  }
  return canvasTexture(canvas, true);
});

/** Nubes suaves (alfa) que repiten: mar de nubes y sombras de nubes. */
const cloudTexture = once(() => {
  const size = 512;
  const [canvas, ctx] = makeCanvas(size);
  ctx.clearRect(0, 0, size, size);
  const rand = seeded(7);
  for (let i = 0; i < 46; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = 30 + rand() * 80;
    drawWrapped(size, x, y, r, (px, py) => {
      const g = ctx.createRadialGradient(px, py, 0, px, py, r);
      g.addColorStop(0, 'rgba(255, 255, 255, 0.75)');
      g.addColorStop(0.6, 'rgba(255, 255, 255, 0.35)');
      g.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(px - r, py - r, r * 2, r * 2);
    });
  }
  const texture = canvasTexture(canvas, true);
  return texture;
});

/** Agua del foso: reflejos claros sobre azul. */
const waterTexture = once(() => {
  const size = 256;
  const [canvas, ctx] = makeCanvas(size);
  ctx.fillStyle = '#2f86c9';
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 160; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const w = 6 + Math.random() * 22;
    ctx.strokeStyle = `rgba(210, 240, 255, ${0.25 + Math.random() * 0.4})`;
    ctx.lineWidth = 1.5;
    drawWrapped(size, x, y, w, (px, py) => {
      ctx.beginPath();
      ctx.moveTo(px - w / 2, py);
      ctx.quadraticCurveTo(px, py - 3, px + w / 2, py);
      ctx.stroke();
    });
  }
  return canvasTexture(canvas, true);
});

// ============================================================ piezas comunes

function makeOuterGround(material: THREE.MeshStandardMaterial): THREE.Mesh {
  const geometry = new THREE.PlaneGeometry(320, 320);
  geometry.rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(worldUv(geometry, GRASS_TILE), material);
  mesh.receiveShadow = true;
  return mesh;
}

function makeRings(floorMat: THREE.Material, centerMat: THREE.Material): { group: THREE.Group; glowMaterials: THREE.MeshBasicMaterial[] } {
  const group = new THREE.Group();
  const glowMaterials: THREE.MeshBasicMaterial[] = [];
  const glowMat = (opacity: number) => {
    const material = new THREE.MeshBasicMaterial({
      color: 0x4d9be0,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    glowMaterials.push(material);
    return material;
  };

  const floorGeometry = new THREE.CircleGeometry(ARENA_RADIUS - 0.2, 96);
  floorGeometry.rotateX(-Math.PI / 2);
  const floor = new THREE.Mesh(worldUv(floorGeometry, GRASS_TILE), floorMat);
  floor.position.y = 0.01;
  floor.receiveShadow = true;
  group.add(floor);

  const outer = new THREE.Mesh(new THREE.RingGeometry(ARENA_RADIUS - 0.35, ARENA_RADIUS, 128), glowMat(0.35));
  outer.rotation.x = -Math.PI / 2;
  outer.position.y = 0.03;
  group.add(outer);

  const centerGeometry = new THREE.CircleGeometry(3.6, 64);
  centerGeometry.rotateX(-Math.PI / 2);
  const center = new THREE.Mesh(worldUv(centerGeometry, STONE_TILE), centerMat);
  center.position.y = 0.02;
  center.receiveShadow = true;
  group.add(center);

  const centerRing = new THREE.Mesh(new THREE.RingGeometry(3.6, 3.78, 64), glowMat(0.6));
  centerRing.rotation.x = -Math.PI / 2;
  centerRing.position.y = 0.035;
  group.add(centerRing);

  const innerRing = new THREE.Mesh(new THREE.RingGeometry(1.2, 1.28, 48), glowMat(0.28));
  innerRing.rotation.x = -Math.PI / 2;
  innerRing.position.y = 0.035;
  group.add(innerRing);

  return { group, glowMaterials };
}

/** Franja plana en XZ de `a` a `b` con media anchura `half`, a la altura `y`. */
function laneStrip(a: { x: number; z: number }, b: { x: number; z: number }, half: number, y: number): THREE.BufferGeometry {
  const length = Math.hypot(b.x - a.x, b.z - a.z);
  const geometry = new THREE.PlaneGeometry(half * 2, length);
  geometry.rotateX(-Math.PI / 2);
  geometry.rotateY(Math.atan2(b.x - a.x, b.z - a.z));
  geometry.translate((a.x + b.x) / 2, y, (a.z + b.z) / 2);
  return geometry;
}

function laneDisc(p: { x: number; z: number }, radius: number, y: number): THREE.BufferGeometry {
  const geometry = new THREE.CircleGeometry(radius, 32);
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(p.x, y, p.z);
  return geometry;
}

/** Carriles pavimentados con bordillo; los codos se tapan con discos. El aspecto lo pone el tema. */
function makeLanes(stone: THREE.Material, curb: THREE.Material): THREE.Group {
  const group = new THREE.Group();
  const curbWidth = 0.35;

  for (const [laneIndex, path] of LANE_PATHS.entries()) {
    const laneGroup = new THREE.Group();
    laneGroup.userData.lane = laneIndex;
    group.add(laneGroup);
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1];
      const b = path[i];
      const border = new THREE.Mesh(laneStrip(a, b, LANE_HALF_WIDTH + curbWidth, 0.013), curb);
      const lane = new THREE.Mesh(worldUv(laneStrip(a, b, LANE_HALF_WIDTH, 0.016), STONE_TILE), stone);
      border.receiveShadow = true;
      lane.receiveShadow = true;
      laneGroup.add(border, lane);
      if (i < path.length - 1) {
        const borderDisc = new THREE.Mesh(laneDisc(b, LANE_HALF_WIDTH + curbWidth, 0.0125), curb);
        const disc = new THREE.Mesh(worldUv(laneDisc(b, LANE_HALF_WIDTH, 0.0155), STONE_TILE), stone);
        borderDisc.receiveShadow = true;
        disc.receiveShadow = true;
        laneGroup.add(borderDisc, disc);
      }
    }
  }

  return group;
}

function makeSpawnGlow(): THREE.Group {
  const group = new THREE.Group();
  const texture = makeGlowTexture();
  const specs: Array<[number, number, number]> = [
    [0, -NEXUS_Z, 0x3f8fe0],
    [0, NEXUS_Z, 0xe05757],
  ];

  for (const [x, z, color] of specs) {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(18, 18),
      new THREE.MeshBasicMaterial({
        map: texture,
        color,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(x, 0.05, z);
    group.add(mesh);
  }

  return group;
}

function makeDust(): { points: THREE.Points } {
  const count = 280;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.sqrt(Math.random()) * (ARENA_RADIUS + 4);
    positions[i * 3] = Math.sin(angle) * radius;
    positions[i * 3 + 1] = Math.random() * 7 + 0.2;
    positions[i * 3 + 2] = Math.cos(angle) * radius;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const material = new THREE.PointsMaterial({
    color: 0xa8d4ff,
    size: 0.07,
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  });

  return { points: new THREE.Points(geometry, material) };
}

/**
 * Tienda en cada base: mostrador con techo hexagonal dorado y una moneda que
 * flota, más el área de compra (radio SHOP.radius alrededor del nexo) en el suelo.
 */
function makeShops(): THREE.Group {
  const group = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: 0x6b4a2a, roughness: 0.8 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0xd9a63a, roughness: 0.45, metalness: 0.3, emissive: 0x3a2400, emissiveIntensity: 0.6 });
  const coinMat = new THREE.MeshBasicMaterial({ color: 0xffd166 });
  const glow = makeGlowTexture();
  for (const side of [-1, 1]) {
    const shop = new THREE.Group();
    shop.position.set(-side * 7.5, 0, side * (NEXUS_Z + 4.5));
    const counter = new THREE.Mesh(new THREE.BoxGeometry(2.6, 1.0, 1.4), wood);
    counter.position.y = 0.5;
    counter.castShadow = true;
    shop.add(counter);
    for (const x of [-1.15, 1.15]) {
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 2.2, 6), wood);
      post.position.set(x, 1.1, 0);
      shop.add(post);
    }
    const roof = new THREE.Mesh(new THREE.ConeGeometry(1.9, 0.9, 6), roofMat);
    roof.position.y = 2.55;
    roof.castShadow = true;
    shop.add(roof);
    const coin = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.08, 24), coinMat);
    coin.rotation.x = Math.PI / 2;
    coin.position.y = 3.6;
    coin.name = 'shop-coin';
    shop.add(coin);
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: glow, color: 0xffc040, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false }));
    halo.position.y = 3.6;
    halo.scale.setScalar(2.4);
    shop.add(halo);
    group.add(shop);

    // área de compra alrededor del nexo propio
    const ringGeometry = new THREE.RingGeometry(SHOP.radius - 0.12, SHOP.radius, 96);
    ringGeometry.rotateX(-Math.PI / 2);
    const ring = new THREE.Mesh(ringGeometry, new THREE.MeshBasicMaterial({ color: 0xffd166, transparent: true, opacity: 0.22, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true }));
    ring.position.set(0, 0.03, side * NEXUS_Z);
    group.add(ring);
  }
  return group;
}

/** Luces de color de cada base (azul abajo, rojo arriba). */
function teamLights(intensity: number): THREE.Group {
  const group = new THREE.Group();
  const blue = new THREE.PointLight(0x4d9be0, intensity, 42, 2);
  blue.position.set(0, 6, -NEXUS_Z + 4);
  const red = new THREE.PointLight(0xe05a5a, intensity, 42, 2);
  red.position.set(0, 6, NEXUS_Z - 4);
  group.add(blue, red);
  return group;
}

/** Color de equipo según el lado del mapa (azul: z < 0). */
function sideColor(z: number): number {
  return z < 0 ? 0x3f7fd8 : 0xd84a4a;
}

// ============================================================ 3v3: reino del castillo

function buildKingdom(): ThemeBuild {
  const group = new THREE.Group();
  group.name = 'theme-kingdom';
  const rand = seeded(1234);

  // luz de mediodía: cielo azul, suelo verde que rebota
  group.add(new THREE.HemisphereLight(0xcfe9ff, 0x3f6a26, 1.25));
  const sun = makeSun(0xfff1d2, 2.5, new THREE.Vector3(18, 34, -14));
  group.add(sun, sun.target, teamLights(26));

  // --- foso de agua alrededor de la muralla
  const water = waterTexture();
  water.repeat.set(1, 1);
  const moatGeometry = new THREE.RingGeometry(ARENA_RADIUS + 1.6, ARENA_RADIUS + 8, 160, 1);
  moatGeometry.rotateX(-Math.PI / 2);
  const moat = new THREE.Mesh(
    worldUv(moatGeometry, 9),
    new THREE.MeshStandardMaterial({ map: water, color: 0xffffff, roughness: 0.2, metalness: 0.15, emissive: 0x0b3050, emissiveIntensity: 0.6 }),
  );
  moat.position.y = 0.02;
  moat.receiveShadow = true;
  group.add(moat);
  // orilla de piedra del foso
  const bankMat = new THREE.MeshStandardMaterial({ color: 0xb9a37a, roughness: 0.9 });
  for (const radius of [ARENA_RADIUS + 1.6, ARENA_RADIUS + 8]) {
    const bank = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.28, 6, 160), bankMat);
    bank.rotation.x = Math.PI / 2;
    bank.position.y = 0.08;
    bank.receiveShadow = true;
    group.add(bank);
  }

  // --- muralla almenada en el borde de la arena
  const wallRadius = ARENA_RADIUS + 0.75;
  const segments = 72;
  const segLength = (2 * Math.PI * wallRadius) / segments + 0.08;
  const wallMatrices: THREE.Matrix4[] = [];
  const merlonMatrices: THREE.Matrix4[] = [];
  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = Math.sin(angle) * wallRadius;
    const z = Math.cos(angle) * wallRadius;
    wallMatrices.push(matrix(x, 0.6, z, 1, 1, 1, angle));
    for (const t of [-0.3, 0.3]) {
      const a = angle + (t * segLength) / wallRadius;
      merlonMatrices.push(matrix(Math.sin(a) * wallRadius, 1.45, Math.cos(a) * wallRadius, 1, 1, 1, a));
    }
  }
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xd8cdb4, roughness: 0.85, flatShading: true });
  group.add(instanced(new THREE.BoxGeometry(segLength, 1.2, 0.9), wallMat, wallMatrices));
  group.add(instanced(new THREE.BoxGeometry(segLength * 0.32, 0.5, 0.95), wallMat, merlonMatrices));

  // torreones con tejado cónico del color de cada lado y banderas
  const towerMat = new THREE.MeshStandardMaterial({ color: 0xe6dcc4, roughness: 0.8, flatShading: true });
  const poleMat = new THREE.MeshStandardMaterial({ color: 0x8a6a3a, roughness: 0.7 });
  const flags: THREE.Mesh[] = [];
  for (let i = 0; i < 10; i++) {
    const angle = ((i + 0.5) / 10) * Math.PI * 2;
    const x = Math.sin(angle) * (wallRadius + 0.3);
    const z = Math.cos(angle) * (wallRadius + 0.3);
    const color = sideColor(z);
    const tower = new THREE.Group();
    tower.position.set(x, 0, z);
    const body = new THREE.Mesh(new THREE.CylinderGeometry(1.35, 1.5, 3.4, 10), towerMat);
    body.position.y = 1.7;
    body.receiveShadow = true;
    const roof = new THREE.Mesh(
      new THREE.ConeGeometry(1.75, 2.1, 10),
      new THREE.MeshStandardMaterial({ color, roughness: 0.55, flatShading: true }),
    );
    roof.position.y = 4.45;
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.4, 5), poleMat);
    pole.position.y = 6.1;
    const flag = new THREE.Mesh(
      new THREE.PlaneGeometry(0.9, 0.5, 6, 1),
      new THREE.MeshStandardMaterial({ color, side: THREE.DoubleSide, roughness: 0.7 }),
    );
    flag.geometry.translate(0.45, 0, 0);
    flag.position.y = 6.5;
    flag.rotation.y = angle;
    flag.userData.phase = rand() * Math.PI * 2;
    flags.push(flag);
    tower.add(body, roof, pole, flag);
    group.add(tower);
  }

  // --- árboles redondos, setos y flores en los huecos entre líneas
  const trunkMatrices: THREE.Matrix4[] = [];
  const canopyMatrices: THREE.Matrix4[] = [];
  const canopyColors: THREE.Color[] = [];
  const bushMatrices: THREE.Matrix4[] = [];
  const bushColors: THREE.Color[] = [];
  const flowerMatrices: THREE.Matrix4[] = [];
  const flowerColors: THREE.Color[] = [];
  const flowerPalette = [0xff5a6e, 0xffd23f, 0xfff4e0, 0xff9ad5, 0x8fb8ff];
  const shadowSpots: Array<{ x: number; z: number; r: number }> = [];
  for (let tries = 0; tries < 2600 && trunkMatrices.length < 70; tries++) {
    const angle = rand() * Math.PI * 2;
    const r = Math.sqrt(rand()) * ARENA_RADIUS;
    const p = { x: Math.sin(angle) * r, z: Math.cos(angle) * r };
    // los árboles sólo en el borde o en claros amplios: no tapan el combate
    if (!freeSpot(p, r > 48 ? 3 : 8, 7)) continue;
    const s = 0.8 + rand() * 0.6;
    trunkMatrices.push(matrix(p.x, 0.7 * s, p.z, s, s, s));
    shadowSpots.push({ x: p.x, z: p.z, r: 1.5 * s });
    const green = new THREE.Color().setHSL(0.27 + rand() * 0.06, 0.55 + rand() * 0.15, 0.3 + rand() * 0.1);
    canopyMatrices.push(matrix(p.x, 2.0 * s, p.z, 1.25 * s, 1.1 * s, 1.25 * s, rand() * 6));
    canopyColors.push(green);
    canopyMatrices.push(matrix(p.x + 0.35 * s, 2.75 * s, p.z - 0.2 * s, 0.85 * s, 0.8 * s, 0.85 * s, rand() * 6));
    canopyColors.push(green.clone().offsetHSL(0, 0, 0.06));
  }
  for (let tries = 0; tries < 3000 && bushMatrices.length < 120; tries++) {
    const angle = rand() * Math.PI * 2;
    const r = Math.sqrt(rand()) * ARENA_RADIUS;
    const p = { x: Math.sin(angle) * r, z: Math.cos(angle) * r };
    if (!freeSpot(p, 1.4, 4.5)) continue;
    const s = 0.45 + rand() * 0.4;
    bushMatrices.push(matrix(p.x, s * 0.45, p.z, s * 1.3, s, s * 1.3, rand() * 6));
    bushColors.push(new THREE.Color().setHSL(0.28 + rand() * 0.05, 0.5, 0.26 + rand() * 0.08));
    shadowSpots.push({ x: p.x, z: p.z, r: 1.2 * s });
    // macizo de flores junto a algunos arbustos
    if (rand() < 0.55) {
      const color = new THREE.Color(flowerPalette[Math.floor(rand() * flowerPalette.length)]);
      for (let k = 0; k < 7; k++) {
        const fx = p.x + (rand() - 0.5) * 2.4;
        const fz = p.z + (rand() - 0.5) * 2.4;
        flowerMatrices.push(matrix(fx, 0.12, fz, 0.12 + rand() * 0.06));
        flowerColors.push(color);
      }
    }
  }
  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x7a5232, roughness: 0.9, flatShading: true });
  const leafMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, flatShading: true });
  group.add(blobShadows(shadowSpots));
  group.add(instanced(new THREE.CylinderGeometry(0.18, 0.28, 1.4, 6), trunkMat, trunkMatrices));
  group.add(instanced(new THREE.IcosahedronGeometry(1, 1), leafMat, canopyMatrices, canopyColors));
  group.add(instanced(new THREE.IcosahedronGeometry(1, 1), leafMat.clone(), bushMatrices, bushColors));
  group.add(
    instanced(new THREE.IcosahedronGeometry(1, 0), new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6 }), flowerMatrices, flowerColors, false),
  );

  // --- más allá del foso: colinas suaves y bosque lejano
  const hillMatrices: THREE.Matrix4[] = [];
  const hillColors: THREE.Color[] = [];
  for (let i = 0; i < 70; i++) {
    const angle = rand() * Math.PI * 2;
    const r = ARENA_RADIUS + 12 + rand() * 45;
    const s = 3 + rand() * 6;
    hillMatrices.push(matrix(Math.sin(angle) * r, -s * 0.35, Math.cos(angle) * r, s * 1.6, s, s * 1.6, rand() * 6));
    hillColors.push(new THREE.Color().setHSL(0.26 + rand() * 0.06, 0.5, 0.28 + rand() * 0.12));
  }
  group.add(instanced(new THREE.IcosahedronGeometry(1, 1), leafMat.clone(), hillMatrices, hillColors, false));

  // --- sombras de nubes que pasan sobre el campo
  const cloudShadowTex = cloudTexture().clone();
  cloudShadowTex.needsUpdate = true;
  const shadowGeometry = new THREE.PlaneGeometry(260, 260);
  shadowGeometry.rotateX(-Math.PI / 2);
  const cloudShadows = new THREE.Mesh(
    worldUv(shadowGeometry, 70),
    new THREE.MeshBasicMaterial({ map: cloudShadowTex, color: 0x000000, transparent: true, opacity: 0.16, depthWrite: false }),
  );
  cloudShadows.position.y = 0.045;
  cloudShadows.renderOrder = 1;
  group.add(cloudShadows);

  return {
    group,
    sun,
    background: new THREE.Color(0x8fcdf2),
    fog: new THREE.FogExp2(0x9fd3f2, 0.0035),
    floor: { map: kingdomGrass(), color: 0xffffff },
    outer: { map: kingdomGrass(), color: 0xb8d8a0 },
    lane: { map: sandstone().map, bump: sandstone().bump, roughness: 0.85, metalness: 0 },
    curb: { color: 0x9a8058 },
    ring: 0xfff2c0,
    dust: 0xfff6c8,
    tick: (_dt, time) => {
      water.offset.set(time * 0.02, time * 0.012);
      cloudShadowTex.offset.set(time * 0.006, time * 0.004);
      for (const flag of flags) flag.rotation.z = Math.sin(time * 3 + (flag.userData.phase as number)) * 0.12;
    },
  };
}

// ============================================================ ARAM: templo sobre las nubes

function buildSky(): ThemeBuild {
  const group = new THREE.Group();
  group.name = 'theme-sky';
  const rand = seeded(4321);

  // atardecer: sol bajo y anaranjado, cielo violeta, contraluz frío
  group.add(new THREE.HemisphereLight(0xffd9ec, 0x4a3a78, 1.05));
  const sun = makeSun(0xffc08a, 2.7, new THREE.Vector3(30, 22, -22));
  const rim = new THREE.DirectionalLight(0x8ea4ff, 0.7);
  rim.position.set(-30, 20, 30);
  group.add(sun, sun.target, rim, teamLights(22));

  // --- la isla: borde de roca, acantilado y raíz rocosa que cuelga
  const rock = rockTexture();
  const rockMat = new THREE.MeshStandardMaterial({ map: rock, color: 0xffe2c8, roughness: 0.95, flatShading: true });
  const cliffGeometry = new THREE.CylinderGeometry(ARENA_RADIUS + 0.2, ARENA_RADIUS - 8, 9, 72, 3, true);
  jitter(cliffGeometry, 0.9, rand);
  const cliff = new THREE.Mesh(cliffGeometry, rockMat);
  cliff.position.y = -4.5;
  cliff.receiveShadow = true;
  group.add(cliff);
  const rootGeometry = new THREE.ConeGeometry(ARENA_RADIUS - 8, 34, 48, 6, true);
  rootGeometry.rotateX(Math.PI);
  jitter(rootGeometry, 2.4, rand);
  const root = new THREE.Mesh(rootGeometry, rockMat);
  root.position.y = -9 - 17;
  group.add(root);
  // labio de hierba sobre el acantilado
  const lip = new THREE.Mesh(
    new THREE.TorusGeometry(ARENA_RADIUS - 0.1, 0.35, 5, 120),
    new THREE.MeshStandardMaterial({ color: 0x6f9c48, roughness: 1, flatShading: true }),
  );
  lip.rotation.x = Math.PI / 2;
  lip.position.y = -0.1;
  group.add(lip);

  // --- columnas de mármol en el borde (algunas rotas) y arcos
  const marbleMat = new THREE.MeshStandardMaterial({ color: 0xf2ebe0, roughness: 0.55, flatShading: true });
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xf2c25a, roughness: 0.4, metalness: 0.1, emissive: 0x6a4410, emissiveIntensity: 0.5 });
  const shaftMatrices: THREE.Matrix4[] = [];
  const baseMatrices: THREE.Matrix4[] = [];
  const capMatrices: THREE.Matrix4[] = [];
  const rubbleMatrices: THREE.Matrix4[] = [];
  const count = 28;
  for (let i = 0; i < count; i++) {
    const angle = ((i + 0.5) / count) * Math.PI * 2;
    const radius = ARENA_RADIUS - 1.2;
    const x = Math.sin(angle) * radius;
    const z = Math.cos(angle) * radius;
    const broken = rand() < 0.4;
    const height = broken ? 1.2 + rand() * 1.6 : 4.2;
    baseMatrices.push(matrix(x, 0.2, z, 1, 1, 1, angle));
    shaftMatrices.push(matrix(x, 0.4 + height / 2, z, 1, height, 1, angle, broken ? (rand() - 0.5) * 0.12 : 0));
    if (!broken) capMatrices.push(matrix(x, 0.4 + height + 0.16, z, 1, 1, 1, angle));
    else {
      // trozos caídos junto a la columna rota
      for (let k = 0; k < 2; k++) {
        const a = angle + (rand() - 0.5) * 0.06;
        const rr = radius - 1.2 - rand() * 1.5;
        rubbleMatrices.push(matrix(Math.sin(a) * rr, 0.25, Math.cos(a) * rr, 0.5 + rand() * 0.4, 0.4, 0.5 + rand() * 0.4, rand() * 6, rand(), rand()));
      }
    }
  }
  group.add(instanced(new THREE.BoxGeometry(1.4, 0.4, 1.4), marbleMat, baseMatrices));
  group.add(instanced(new THREE.CylinderGeometry(0.45, 0.52, 1, 12), marbleMat, shaftMatrices));
  group.add(instanced(new THREE.BoxGeometry(1.3, 0.32, 1.3), goldMat, capMatrices));
  group.add(instanced(new THREE.DodecahedronGeometry(1, 0), marbleMat, rubbleMatrices));

  // --- ruinas en los costados (en ARAM sólo se usa la línea central)
  const midClear = (p: { x: number; z: number }, clear: number) => {
    const r = Math.hypot(p.x, p.z);
    if (r < 6 || r > ARENA_RADIUS - 3) return false;
    if (Math.abs(p.x) < LANE_HALF_WIDTH + clear) return false;
    for (const side of [-1, 1]) if (Math.hypot(p.x, p.z - side * NEXUS_Z) < SHOP.radius + 3) return false;
    return true;
  };
  // losas de mármol sueltas (plazas en ruinas)
  const slabMatrices: THREE.Matrix4[] = [];
  for (let tries = 0; tries < 2000 && slabMatrices.length < 160; tries++) {
    const cx = (rand() < 0.5 ? -1 : 1) * (12 + rand() * 40);
    const cz = (rand() - 0.5) * 90;
    const center = { x: cx, z: cz };
    if (!midClear(center, 6)) continue;
    for (let k = 0; k < 8; k++) {
      const p = { x: cx + Math.round((rand() - 0.5) * 6) * 1.6, z: cz + Math.round((rand() - 0.5) * 6) * 1.6 };
      if (!midClear(p, 5)) continue;
      slabMatrices.push(matrix(p.x, 0.03, p.z, 1, 1, 1, 0));
    }
  }
  group.add(instanced(new THREE.BoxGeometry(1.5, 0.08, 1.5), new THREE.MeshStandardMaterial({ color: 0xe9e0d2, roughness: 0.5 }), slabMatrices, undefined, false));

  // dos templetes en ruinas a los lados del centro
  const skyShadows: Array<{ x: number; z: number; r: number }> = [];
  for (const sx of [-1, 1]) {
    const temple = new THREE.Group();
    temple.position.set(sx * 34, 0, sx * 6);
    const stepA = new THREE.Mesh(new THREE.BoxGeometry(11, 0.35, 8), marbleMat);
    stepA.position.y = 0.17;
    const stepB = new THREE.Mesh(new THREE.BoxGeometry(9.6, 0.35, 6.6), marbleMat);
    stepB.position.y = 0.52;
    stepA.receiveShadow = stepB.receiveShadow = true;
    temple.add(stepA, stepB);
    for (const [cx, cz] of [[-4, -2.6], [-1.35, -2.6], [1.35, -2.6], [4, -2.6], [-4, 2.6], [4, 2.6], [-1.35, 2.6], [1.35, 2.6]]) {
      const broken = rand() < 0.35;
      const h = broken ? 1 + rand() * 1.5 : 3.6;
      const column = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.44, h, 12), marbleMat);
      column.position.set(cx, 0.7 + h / 2, cz);
      temple.add(column);
      skyShadows.push({ x: sx * 34 + cx, z: sx * 6 + cz, r: 0.9 });
      if (!broken) {
        const cap = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.25, 1.0), goldMat);
        cap.position.set(cx, 0.7 + h + 0.12, cz);
        temple.add(cap);
      }
    }
    // dintel a medio caer
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(6.2, 0.45, 0.9), marbleMat);
    lintel.position.set(-1.3, 4.75, -2.6);
    lintel.rotation.z = 0.05;
    temple.add(lintel);
    // altar con una esfera de luz
    const altar = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1, 1.4), marbleMat);
    altar.position.y = 1.2;
    const orb = new THREE.Mesh(new THREE.SphereGeometry(0.38, 16, 12), new THREE.MeshBasicMaterial({ color: 0xffe6a0 }));
    orb.position.y = 2.1;
    orb.name = 'sky-orb';
    const orbGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeGlowTexture(), color: 0xffd27a, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false }));
    orbGlow.position.y = 2.1;
    orbGlow.scale.setScalar(2.6);
    temple.add(altar, orb, orbGlow);
    group.add(temple);
  }

  // árboles en flor y arbustos
  const trunkMatrices: THREE.Matrix4[] = [];
  const blossomMatrices: THREE.Matrix4[] = [];
  const blossomColors: THREE.Color[] = [];
  for (let tries = 0; tries < 2500 && trunkMatrices.length < 46; tries++) {
    const angle = rand() * Math.PI * 2;
    const r = Math.sqrt(rand()) * ARENA_RADIUS;
    const p = { x: Math.sin(angle) * r, z: Math.cos(angle) * r };
    if (!midClear(p, 8)) continue;
    if (Math.abs(Math.abs(p.x) - 34) < 7 && Math.abs(p.z - Math.sign(p.x) * 6) < 6) continue;
    const s = 0.8 + rand() * 0.5;
    trunkMatrices.push(matrix(p.x, 0.7 * s, p.z, s));
    skyShadows.push({ x: p.x, z: p.z, r: 1.5 * s });
    const pink = new THREE.Color().setHSL(0.92 + rand() * 0.05, 0.6 + rand() * 0.2, 0.72 + rand() * 0.1);
    blossomMatrices.push(matrix(p.x, 2.0 * s, p.z, 1.3 * s, 1.0 * s, 1.3 * s, rand() * 6));
    blossomColors.push(pink);
    blossomMatrices.push(matrix(p.x - 0.4 * s, 2.6 * s, p.z + 0.3 * s, 0.8 * s, 0.7 * s, 0.8 * s, rand() * 6));
    blossomColors.push(pink.clone().offsetHSL(0, 0, 0.06));
    if (rand() < 0.7) {
      blossomMatrices.push(matrix(p.x + 1.2 * s, 0.35, p.z + 0.6 * s, 0.6, 0.45, 0.6, rand() * 6));
      blossomColors.push(new THREE.Color().setHSL(0.3, 0.45, 0.32));
    }
  }
  group.add(blobShadows(skyShadows));
  group.add(instanced(new THREE.CylinderGeometry(0.16, 0.26, 1.4, 6), new THREE.MeshStandardMaterial({ color: 0x6a4a3a, roughness: 0.9, flatShading: true }), trunkMatrices));
  group.add(instanced(new THREE.IcosahedronGeometry(1, 1), new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, flatShading: true }), blossomMatrices, blossomColors));

  // braseros dorados con llama a los lados de cada base
  const flameTex = makeGlowTexture();
  const flames: THREE.Sprite[] = [];
  for (const side of [-1, 1]) {
    for (const sx of [-1, 1]) {
      const x = sx * 12;
      const z = side * (NEXUS_Z - 6);
      const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.35, 0.5, 10), goldMat);
      bowl.position.set(x, 1.55, z);
      const stand = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.2, 1.3, 6), goldMat);
      stand.position.set(x, 0.65, z);
      const flame = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: flameTex, color: side < 0 ? 0x7fb8ff : 0xff8a5a, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }),
      );
      flame.position.set(x, 2.2, z);
      flame.scale.setScalar(1.6);
      flame.userData.phase = rand() * 6;
      flames.push(flame);
      const light = new THREE.PointLight(side < 0 ? 0x7fb8ff : 0xff8a5a, 8, 12, 2);
      light.position.set(x, 2.4, z);
      group.add(bowl, stand, flame, light);
    }
  }

  // --- mar de nubes debajo (dos capas que se mueven) y fondo
  const clouds = cloudTexture();
  const cloudA = clouds.clone();
  const cloudB = clouds.clone();
  cloudA.needsUpdate = true;
  cloudB.needsUpdate = true;
  const seaGeometry = (size: number, tile: number) => {
    const g = new THREE.PlaneGeometry(size, size);
    g.rotateX(-Math.PI / 2);
    return worldUv(g, tile);
  };
  const deep = new THREE.Mesh(seaGeometry(600, 100), new THREE.MeshBasicMaterial({ color: 0x4b3a8e }));
  deep.position.y = -46;
  const seaLow = new THREE.Mesh(
    seaGeometry(600, 90),
    new THREE.MeshBasicMaterial({ map: cloudA, color: 0xa684e0, transparent: true, opacity: 0.9, depthWrite: false }),
  );
  seaLow.position.y = -38;
  const seaHigh = new THREE.Mesh(
    seaGeometry(600, 60),
    new THREE.MeshBasicMaterial({ map: cloudB, color: 0xff9f7a, transparent: true, opacity: 0.75, depthWrite: false }),
  );
  seaHigh.position.y = -22;
  group.add(deep, seaLow, seaHigh);

  // --- rocas flotantes alrededor
  const floaters: THREE.Mesh[] = [];
  for (let i = 0; i < 16; i++) {
    const angle = rand() * Math.PI * 2;
    const r = ARENA_RADIUS + 10 + rand() * 40;
    const s = 1.2 + rand() * 3.2;
    const geometry = new THREE.DodecahedronGeometry(s, 1);
    jitter(geometry, s * 0.25, rand);
    geometry.scale(1, 0.6, 1);
    const boulder = new THREE.Mesh(geometry, rockMat);
    boulder.position.set(Math.sin(angle) * r, -3 - rand() * 12, Math.cos(angle) * r);
    boulder.rotation.set(rand(), rand() * 6, rand() * 0.3);
    boulder.userData.base = boulder.position.y;
    boulder.userData.phase = rand() * 6;
    // tapa de hierba en las grandes
    if (s > 2.4) {
      const top = new THREE.Mesh(new THREE.CylinderGeometry(s * 0.8, s * 0.9, 0.3, 9), lip.material);
      top.position.y = s * 0.55;
      boulder.add(top);
    }
    floaters.push(boulder);
    group.add(boulder);
  }

  return {
    group,
    sun,
    background: new THREE.Color(0x3a2d6e),
    fog: null,
    floor: { map: skyGrass(), color: 0xffffff },
    outer: null,
    lane: { map: marble().map, bump: marble().bump, roughness: 0.45, metalness: 0.05 },
    curb: { color: 0xd9b25a },
    ring: 0xffd27a,
    dust: 0xffd88a,
    tick: (_dt, time) => {
      cloudA.offset.set(time * 0.004, time * 0.002);
      cloudB.offset.set(-time * 0.007, time * 0.003);
      for (const boulder of floaters) {
        const phase = boulder.userData.phase as number;
        boulder.position.y = (boulder.userData.base as number) + Math.sin(time * 0.5 + phase) * 0.6;
        boulder.rotation.y += 0.0008;
      }
      for (const flame of flames) {
        const phase = flame.userData.phase as number;
        flame.scale.setScalar(1.5 + Math.sin(time * 9 + phase) * 0.15 + Math.sin(time * 13 + phase) * 0.08);
      }
    },
  };
}

/** Desplaza los vértices al azar (roca irregular), compartiendo el desplazamiento en vértices coincidentes. */
function jitter(geometry: THREE.BufferGeometry, amount: number, rand: () => number): void {
  const pos = geometry.getAttribute('position');
  const seen = new Map<string, [number, number, number]>();
  for (let i = 0; i < pos.count; i++) {
    const key = `${pos.getX(i).toFixed(3)},${pos.getY(i).toFixed(3)},${pos.getZ(i).toFixed(3)}`;
    let offset = seen.get(key);
    if (!offset) {
      offset = [(rand() - 0.5) * amount, (rand() - 0.5) * amount, (rand() - 0.5) * amount];
      seen.set(key, offset);
    }
    pos.setXYZ(i, pos.getX(i) + offset[0], pos.getY(i) + offset[1], pos.getZ(i) + offset[2]);
  }
  geometry.computeVertexNormals();
}

// ============================================================ entrenamiento: sala holográfica

function buildTraining(): ThemeBuild {
  const group = new THREE.Group();
  group.name = 'theme-training';

  group.add(new THREE.HemisphereLight(0xd6e6ff, 0x101828, 1.0));
  const sun = makeSun(0xf2f6ff, 1.8, new THREE.Vector3(14, 30, -10));
  group.add(sun, sun.target, teamLights(18));

  // muro de luz alrededor: degradado vertical con barrido de líneas
  const wallMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    uniforms: { uTime: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      varying vec2 vUv;
      void main() {
        float fade = pow(1.0 - vUv.y, 1.6);
        float scan = 0.55 + 0.45 * sin(vUv.y * 60.0 - uTime * 4.0);
        float cols = smoothstep(0.92, 1.0, abs(sin(vUv.x * 3.14159 * 64.0)));
        float alpha = fade * (0.32 * scan + 0.5 * cols) + smoothstep(0.03, 0.0, vUv.y) * 0.8;
        gl_FragColor = vec4(vec3(0.35, 0.85, 1.0) * alpha, alpha);
      }
    `,
  });
  const wall = new THREE.Mesh(new THREE.CylinderGeometry(ARENA_RADIUS + 0.4, ARENA_RADIUS + 0.4, 3, 128, 1, true), wallMaterial);
  wall.position.y = 1.5;
  group.add(wall);

  // pilones con franja luminosa del color de cada lado
  const pylonMat = new THREE.MeshStandardMaterial({ color: 0x24324a, roughness: 0.45, metalness: 0.2, emissive: 0x0a1a2a });
  for (let i = 0; i < 12; i++) {
    const angle = ((i + 0.5) / 12) * Math.PI * 2;
    const x = Math.sin(angle) * (ARENA_RADIUS + 2.2);
    const z = Math.cos(angle) * (ARENA_RADIUS + 2.2);
    const pylon = new THREE.Mesh(new THREE.BoxGeometry(1.2, 4.2, 1.2), pylonMat);
    pylon.position.set(x, 2.1, z);
    pylon.rotation.y = angle;
    const strip = new THREE.Mesh(new THREE.BoxGeometry(1.24, 0.18, 1.24), new THREE.MeshBasicMaterial({ color: sideColor(z) }));
    strip.position.set(x, 3.6, z);
    strip.rotation.y = angle;
    group.add(pylon, strip);
  }

  // cubos holográficos flotando fuera de la sala
  const cubes: THREE.Mesh[] = [];
  const rand = seeded(99);
  const cubeMat = new THREE.MeshBasicMaterial({ color: 0x58c8ff, wireframe: true, transparent: true, opacity: 0.45 });
  for (let i = 0; i < 14; i++) {
    const angle = rand() * Math.PI * 2;
    const r = ARENA_RADIUS + 8 + rand() * 30;
    const cube = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), cubeMat);
    const s = 1 + rand() * 2.5;
    cube.scale.setScalar(s);
    cube.position.set(Math.sin(angle) * r, 2 + rand() * 6, Math.cos(angle) * r);
    cube.userData.spin = (rand() - 0.5) * 0.8;
    cubes.push(cube);
    group.add(cube);
  }

  const floor = trainingFloor();
  const lane = trainingLane();
  return {
    group,
    sun,
    background: new THREE.Color(0x04070d),
    fog: new THREE.FogExp2(0x04070d, 0.004),
    floor: { map: floor.map, color: 0xffffff, emissiveMap: floor.emissive, emissive: 0x2f9ad0 },
    outer: { map: floor.map, color: 0x6a7a90, emissiveMap: floor.emissive, emissive: 0x153a55 },
    lane: { map: lane.map, emissiveMap: lane.emissive, emissive: 0x4ab8e8, roughness: 0.35, metalness: 0.4 },
    curb: { color: 0x10202e, emissive: 0x1d6f9a },
    ring: 0x58c8ff,
    dust: 0x7fdcff,
    tick: (dt, time) => {
      wallMaterial.uniforms.uTime.value = time;
      for (const cube of cubes) {
        cube.rotation.x += dt * (cube.userData.spin as number);
        cube.rotation.y += dt * 0.3;
      }
    },
  };
}
